import type { AssertNoInvalidExpandKeys, Collection, CustomVertexExport } from './util';
import type { TeacherVertex } from './teachers';
import type { ExceptionVertex } from './exceptions';
import type { EventVertex, ScheduleEventVertex, CalendarEventVertex } from './events';
import type { CourseVertex } from './courses';
import type { DivisionSettingsVertex } from './division-settings';
import type { DivisionVertex } from './divisions';
import type { GenerationVertex } from './generations';
import type { GroupVertex } from './groups';
import type { LocationVertex } from './locations';
import type { LockedTimeVertex } from './locked-times';
import type { LinkedEventsSetVertex } from './linked-events-sets';
import type { PeriodVertex } from './periods';
import type { PersonVertex } from './persons';
import type { RootIntervalVertex } from './root-intervals';
import type { SyllabusVertex } from './syllabuses';
import type { ConfigurationVertex } from './configurations';
import type { CompanyVertex } from './companies';
import type { UserVertex } from './users';
import type { ThreadVertex } from './threads';
import type { ConversationVertex } from './conversations';
import type { MessageVertex } from './messages';
import type { DutyVertex } from './duties';
import type { VertexQuery as Query } from './util/vertex-query';
import type { TypeEqual } from 'ts-expect';
import { expectType } from 'ts-expect';


export type VertexRecord = {
  divisions:        DivisionVertex;
  divisionSettings: DivisionSettingsVertex;
  settings:         DivisionSettingsVertex;
  duties:           DutyVertex;
  teachers:         TeacherVertex;
  exceptions:       ExceptionVertex;
  events:           EventVertex;
  scheduleEvents:   ScheduleEventVertex;
  calendarEvents:   CalendarEventVertex;
  courses:          CourseVertex;
  generations:      GenerationVertex;
  groups:           GroupVertex;
  locations:        LocationVertex;
  lockedTimes:      LockedTimeVertex;
  linkedEventsSets: LinkedEventsSetVertex;
  periods:          PeriodVertex;
  persons:          PersonVertex;
  students:         PersonVertex;
  rootIntervals:    RootIntervalVertex;
  syllabuses:       SyllabusVertex;
  configurations:   ConfigurationVertex;
  companies:        CompanyVertex;
  users:            UserVertex;
  threads:          ThreadVertex;
  conversations:    ConversationVertex;
  messages:         MessageVertex;
};
export type BaseQueryRecord<C extends keyof VertexRecord> = Query<VertexRecord[C]>;
{
  // the keys must all be a collection
  type Mismatches = keyof {
    [K in keyof VertexRecord as K extends Collection ? never : K]: true
  };
  expectType<TypeEqual<Mismatches, never>>(true);
}

/**
 * See {@link createVertexQuery}
 */
export class VertexQuery <
  C extends keyof VertexRecord,
  Q extends BaseQueryRecord<C> & AssertNoInvalidExpandKeys<VertexRecord[C], Q>
> {
  constructor (
    private readonly _collection: C,
    private readonly _query:      Q
  ) {}

  get collection () { return this._collection }
  get query      () { return this._query      }
}


/**
 * Infers the result type for a query created via createVertexQuery.
 *
 * @example
 * const personQuery = createVertexQuery('persons', { select: ['id', 'firstName'] });
 * type PersonType = Infer<typeof personQuery>;
 */
export type Infer<T extends VertexQuery<any, any>> = // eslint-disable-line @typescript-eslint/no-explicit-any
  T extends VertexQuery<infer C, infer Q>
    ? CustomVertexExport<VertexRecord[C], Q>
    : never;


/**
 * Builds a typed vertex query for a given vertex type using a select/expand shape.
 *
 * Use together with Infer<T> to obtain the serialized result type.
 *
 * @typeParam Q - The select/expand query shape for that vertex.
 *
 * @example
 * const groupQuery = createVertexQuery.group({
 *   select: ['id', 'displayName'],
 *   expand: { members: { select: ['id'] } }
 * });
 * type GroupType = Infer<typeof groupQuery>;
 */
export namespace createVertexQuery {
  //
  // The following did not work: it seems we need to define Q as an completely independent type parameter (?)
  //
  // export function createVertexQuery <
  //   C extends keyof VertexRecord,
  //   const Q extends Query<VertexRecord[C]>
  // > (
  //   collection: C,
  //   query:      Q
  // ) {
  //   return new VertexQuery(collection, query);
  // }

  export function division       <Q extends Query<DivisionVertex        > & AssertNoInvalidExpandKeys<DivisionVertex,         Q>> (query: Q) { return new VertexQuery('divisions',        query) }
  export function duty           <Q extends Query<DutyVertex            > & AssertNoInvalidExpandKeys<DutyVertex,             Q>> (query: Q) { return new VertexQuery('duties',            query) }
  export function settings       <Q extends Query<DivisionSettingsVertex> & AssertNoInvalidExpandKeys<DivisionSettingsVertex, Q>> (query: Q) { return new VertexQuery('settings',         query) }
  export function exception      <Q extends Query<ExceptionVertex       > & AssertNoInvalidExpandKeys<ExceptionVertex,        Q>> (query: Q) { return new VertexQuery('exceptions',       query) }
  export function event          <Q extends Query<EventVertex           > & AssertNoInvalidExpandKeys<EventVertex,            Q>> (query: Q) { return new VertexQuery('events',           query) }
  export function scheduleEvent  <Q extends Query<ScheduleEventVertex   > & AssertNoInvalidExpandKeys<ScheduleEventVertex,    Q>> (query: Q) { return new VertexQuery('scheduleEvents',   query) }
  export function calendarEvent  <Q extends Query<CalendarEventVertex   > & AssertNoInvalidExpandKeys<CalendarEventVertex,    Q>> (query: Q) { return new VertexQuery('calendarEvents',   query) }
  export function course         <Q extends Query<CourseVertex          > & AssertNoInvalidExpandKeys<CourseVertex,           Q>> (query: Q) { return new VertexQuery('courses',          query) }
  export function generation     <Q extends Query<GenerationVertex      > & AssertNoInvalidExpandKeys<GenerationVertex,       Q>> (query: Q) { return new VertexQuery('generations',      query) }
  export function location       <Q extends Query<LocationVertex        > & AssertNoInvalidExpandKeys<LocationVertex,         Q>> (query: Q) { return new VertexQuery('locations',        query) }
  export function lockedTime     <Q extends Query<LockedTimeVertex      > & AssertNoInvalidExpandKeys<LockedTimeVertex,       Q>> (query: Q) { return new VertexQuery('lockedTimes',      query) }
  export function linkedEventsSet<Q extends Query<LinkedEventsSetVertex > & AssertNoInvalidExpandKeys<LinkedEventsSetVertex,  Q>> (query: Q) { return new VertexQuery('linkedEventsSets', query) }
  export function period         <Q extends Query<PeriodVertex          > & AssertNoInvalidExpandKeys<PeriodVertex,           Q>> (query: Q) { return new VertexQuery('periods',          query) }
  export function person         <Q extends Query<PersonVertex          > & AssertNoInvalidExpandKeys<PersonVertex,           Q>> (query: Q) { return new VertexQuery('persons',          query) }
  export function student        <Q extends Query<PersonVertex          > & AssertNoInvalidExpandKeys<PersonVertex,           Q>> (query: Q) { return new VertexQuery('students',         query) }
  export function rootInterval   <Q extends Query<RootIntervalVertex    > & AssertNoInvalidExpandKeys<RootIntervalVertex,     Q>> (query: Q) { return new VertexQuery('rootIntervals',    query) }
  export function syllabus       <Q extends Query<SyllabusVertex        > & AssertNoInvalidExpandKeys<SyllabusVertex,         Q>> (query: Q) { return new VertexQuery('syllabuses',       query) }
  export function group          <Q extends Query<GroupVertex           > & AssertNoInvalidExpandKeys<GroupVertex,            Q>> (query: Q) { return new VertexQuery('groups',           query) }
  export function teacher        <Q extends Query<TeacherVertex         > & AssertNoInvalidExpandKeys<TeacherVertex,          Q>> (query: Q) { return new VertexQuery('teachers',         query) }
  export function configuration  <Q extends Query<ConfigurationVertex   > & AssertNoInvalidExpandKeys<ConfigurationVertex,    Q>> (query: Q) { return new VertexQuery('configurations',   query) }
  export function thread         <Q extends Query<ThreadVertex          > & AssertNoInvalidExpandKeys<ThreadVertex,           Q>> (query: Q) { return new VertexQuery('threads',          query) }
  export function user           <Q extends Query<UserVertex            > & AssertNoInvalidExpandKeys<UserVertex,             Q>> (query: Q) { return new VertexQuery('users',            query) }
  export function conversation   <Q extends Query<ConversationVertex    > & AssertNoInvalidExpandKeys<ConversationVertex,     Q>> (query: Q) { return new VertexQuery('conversations',    query) }
  export function message        <Q extends Query<MessageVertex         > & AssertNoInvalidExpandKeys<MessageVertex,          Q>> (query: Q) { return new VertexQuery('messages',         query) }
}